import { pxPerMeter } from "config/constants";
import { pxPerFoot, UnitSystem } from "shared/util/units";
import { backgroundMargin, gridEdgeTolerance, sideOffset } from "./editorConstants";

// only the plan's size is needed here, so the geometry can be asked for from inside the
// context provider too - where the editor data object does not exist yet (see resetCanvasMove)
export type PlanSize = { width: number; height: number };

// Where the grid's lines go and how far they reach. The lines and the numbers beside them are
// drawn by two different functions, and a number is only readable as part of its line while
// both agree on where that line starts and ends - so the geometry is settled once, here.

// One line per unit from the plan's near edge, up to its far edge, and one more when the plan
// ends between two lines - a whole step on, so every line keeps its distance and the last strip
// is not cut short. That closing line only earns its place within the background's own margin
// past the plan: on the coarser meter grid, a whole step on can overshoot the margin by more
// than the margin itself, which would stretch the background just to fit a line nothing needs -
// so past the margin, the last strip is left open instead.
function linePositions(from: number, to: number, step: number, marginEdge: number): number[] {
  const positions: number[] = [];

  for (let at = from; at <= to + gridEdgeTolerance; at += step) {
    positions.push(at);
  }
  const closing = positions[positions.length - 1] + step;
  if (positions[positions.length - 1] < to - gridEdgeTolerance && closing <= marginEdge + gridEdgeTolerance) {
    positions.push(closing);
  }

  return positions;
}

// The grid follows the unit the plan is shown in: one line per meter, or one per foot
// (22.86 px instead of 75 - see F3 of US-IMPERIAL-TERV.md).
export function gridGeometry(plan: PlanSize, unit: UnitSystem) {
  const step = unit === "IMPERIAL" ? pxPerFoot : pxPerMeter;
  const marginPx = backgroundMargin * pxPerMeter;

  const rightEdge = sideOffset.left + plan.width * pxPerMeter;
  const bottomEdge = sideOffset.top + plan.height * pxPerMeter;

  const verticals = linePositions(sideOffset.left, rightEdge, step, rightEdge + marginPx);
  const horizontals = linePositions(sideOffset.top, bottomEdge, step, bottomEdge + marginPx);

  // the grid spans the background; the closing line never reaches past it, now that it is
  // itself kept within the margin
  return {
    step,
    verticals,
    horizontals,
    right: Math.max((plan.width + 2 * backgroundMargin) * pxPerMeter, verticals[verticals.length - 1]),
    bottom: Math.max((plan.height + 2 * backgroundMargin) * pxPerMeter, horizontals[horizontals.length - 1]),
  };
}
